import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../utils/icons';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import { useCurrentUser } from '../hooks/useCurrentUser';

type CoreItem = 'calendar' | 'discoveries' | 'todos' | 'weather' | 'today' | 'account';

interface CoreNavRailProps {
  active?: CoreItem;
}

const navItems: Array<{ id: CoreItem; label: string; icon: any; path: string; isTodayNav?: boolean; isAvatar?: boolean }> = [
  { id: 'calendar', label: 'Calendar', icon: 'calendar', path: '/' },
  { id: 'discoveries', label: 'Discoveries', icon: 'discoveries', path: '/discoveries' },
  { id: 'todos', label: 'To Dos', icon: 'check', path: '/todos' },
  { id: 'weather', label: 'Weather', icon: 'weather', path: '/weather' },
  { id: 'today', label: 'Today', icon: 'home', path: '/today', isTodayNav: true },
  { id: 'account', label: 'Account', icon: 'settings', path: '/settings', isAvatar: true },
];

export function CoreNavRail({ active }: CoreNavRailProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const { avatar, initials, color } = useCurrentUser();

  const currentActive =
    active ||
    (navItems.find((item) => location.pathname === item.path || (item.isTodayNav && location.pathname === '/today'))?.id as CoreItem) ||
    (navItems.find((item) => !item.isTodayNav && location.pathname.startsWith(item.path))?.id as CoreItem) ||
    (location.pathname === '/family' || location.pathname === '/whats-new' ? 'account' : undefined) ||
    'calendar';

  const handleNavClick = (item: typeof navItems[0]) => {
    navigate(item.path);
  };

  return (
    <div className={`fixed right-0 top-0 bottom-0 z-40 w-20 ${tokens.border.light} border-l ${tokens.bg.panel} backdrop-blur ${tokens.card.shadow} rounded-landing-l flex flex-col items-center justify-between gap-3 p-2`}>
      {/* Navigation Buttons */}
      <div className="flex flex-col items-center gap-3">
        {navItems.map((item) => {
          const isActive = currentActive === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              className={`w-14 h-14 flex flex-col items-center justify-center touch-manipulation transition-all active:scale-95 ${
                item.isAvatar ? 'rounded-full' : 'rounded-landing gap-1'
              } ${
                item.isAvatar && !isActive
                  ? 'bg-transparent'
                  : isActive
                    ? `${tokens.button.primary} ${tokens.text.inverse} shadow-md`
                    : `${tokens.button.secondary} ${tokens.text.primary}`
              }`}
              aria-label={item.label}
            >
              {item.isAvatar ? (
                <div className="w-full h-full flex items-center justify-center p-0.5 box-border">
                  <div className="relative w-full h-full max-w-[40px] max-h-[40px] rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md overflow-hidden box-border" style={{ backgroundColor: color }}>
                    {!avatar && initials}
                    {avatar && (
                      <img
                        src={avatar}
                        alt={item.label}
                        className="absolute inset-0 w-full h-full rounded-full object-cover box-border"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <Icon
                    name={item.icon as any}
                    size="lg"
                    className={isActive ? tokens.text.inverse : tokens.text.primary}
                  />
                  <span className={`text-[11px] font-semibold ${isActive ? tokens.text.inverse : tokens.text.primary}`}>
                    {item.label}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
      
      {/* Runow Brand - Bottom Aligned, Vertical */}
      <span 
        className={`${tokens.text.muted} text-[60px] font-serif ${tokens.card.shadow} bg-transparent`}
        style={{ 
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          backgroundColor: 'transparent',
          background: 'none',
          display: 'block'
        }}
      >
        Runow
      </span>
    </div>
  );
}
